import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import { buildBulkFailureHtml, getBulkFailures, runChunkedBulkRequest } from "./bulk-api";
import { getAccountingTransactionText } from "./accounting-transaction-text";
import { handleExcelGridKeyDown } from "./excel-grid-navigation";
import { TRF_SECTIONS, buildTrfIndex, fetchTrfEntries, findTrfMatches, getDateBounds, getTrfSectionLabel } from "./trf-duplicate-check";

const GRID_COLUMNS = ["date", "description", "amount"];

// Tujuan sama dengan section tab Trf di Menu Pembukuan.
export const TRF_TARGETS = TRF_SECTIONS;

const formatNumber = (value) => Number(value || 0).toLocaleString("id-ID");
const parseNumberInput = (value) => Number(String(value || "").replace(/[^0-9]/g, "")) || 0;
const toDateOnly = (value) => (value ? String(value).slice(0, 10) : "");

const getReservationIncomeId = (row) =>
  row?.reservation_income_id || row?.reservation_income?.id || null;

const AccountingV2TrfModal = ({ show, rows, onHide, onDone }) => {
  const [section, setSection] = useState(TRF_TARGETS[0].section);
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState({ active: false, current: 0, total: 0 });
  // Nominal yang sedang diedit ditampilkan mentah (tanpa titik) supaya kursor tidak loncat.
  const [focusedAmountIndex, setFocusedAmountIndex] = useState(null);
  // Data Trf yang sudah ada, untuk mencegah transaksi yang sama disalin dua kali.
  const [trfIndex, setTrfIndex] = useState(null);
  const [duplicateCheck, setDuplicateCheck] = useState({ loading: false, error: "" });

  useEffect(() => {
    if (!show) return;
    // Default: tanggal = tanggal transaksi Accounting V2, nominal dikosongkan (diisi nanti di Pembukuan).
    setItems(rows.map((row) => ({
      reservationId: row.id,
      reservationIncomeId: getReservationIncomeId(row),
      date: toDateOnly(row.reservation_date),
      description: getAccountingTransactionText(row),
      amount: 0,
      include: true,
    })));
    setProgress({ active: false, current: 0, total: 0 });
  }, [show, rows]);

  useEffect(() => {
    if (!show) return undefined;
    const bounds = getDateBounds(rows.map((row) => row.reservation_date));
    if (!bounds) return undefined;
    let cancelled = false;

    setTrfIndex(null);
    setDuplicateCheck({ loading: true, error: "" });
    fetchTrfEntries(bounds)
      .then((entries) => {
        if (!cancelled) setTrfIndex(buildTrfIndex(entries));
      })
      .catch((error) => {
        if (!cancelled) setDuplicateCheck({ loading: false, error: error?.message || "Gagal mengecek data dobel." });
      })
      .finally(() => {
        if (!cancelled) setDuplicateCheck((current) => ({ ...current, loading: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [show, rows]);

  // Data Trf yang sama untuk setiap baris (di tab tujuan maupun tab Trf lain).
  const itemMatches = useMemo(() => items.map((item) => {
    if (!trfIndex) return { inTarget: [], inOthers: [] };
    const matches = findTrfMatches({ reservationId: item.reservationId, text: item.description, date: item.date }, trfIndex);
    return {
      inTarget: matches.filter((match) => match.section === section),
      inOthers: matches.filter((match) => match.section !== section),
    };
  }), [items, trfIndex, section]);

  // Setiap hasil cek / tujuan berubah: yang sudah ada di tab tujuan otomatis tidak dicentang.
  useEffect(() => {
    if (!trfIndex) return;
    setItems((current) => current.map((item) => {
      const matches = findTrfMatches({ reservationId: item.reservationId, text: item.description, date: item.date }, trfIndex);
      return { ...item, include: !matches.some((match) => match.section === section) };
    }));
  }, [trfIndex, section]);

  const updateItem = (index, field, value) => {
    setItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)));
  };

  const includedItems = items.filter((item) => item.include);
  const duplicateInTargetCount = itemMatches.filter((match) => match.inTarget.length > 0).length;
  const includedDuplicateCount = items.filter((item, index) => item.include && itemMatches[index]?.inTarget.length > 0).length;
  const totalAmount = includedItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const targetLabel = TRF_TARGETS.find((target) => target.section === section)?.label;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const itemsToSave = items.filter((item) => item.include);
    if (itemsToSave.length === 0) {
      Swal.fire("Tidak ada yang disalin", "Centang minimal 1 transaksi untuk disalin.", "info");
      return;
    }
    const invalid = itemsToSave.find((item) => !item.date);
    if (invalid) {
      Swal.fire("Data belum lengkap", "Setiap transaksi wajib punya tanggal.", "warning");
      return;
    }
    if (includedDuplicateCount > 0) {
      const confirmation = await Swal.fire({
        icon: "warning",
        title: "Ada transaksi dobel",
        text: `${includedDuplicateCount} transaksi sudah ada di ${targetLabel}. Server akan menolaknya sebagai dobel, transaksi lain tetap disimpan. Lanjutkan?`,
        showCancelButton: true,
        confirmButtonText: "Lanjutkan",
        cancelButtonText: "Cek lagi",
      });
      if (!confirmation.isConfirmed) return;
    }

    // 1 request bulk (dipecah per 500 item kalau lebih), urutan item = urutan baris di Pembukuan.
    setProgress({ active: true, current: 0, total: itemsToSave.length });
    let response;
    try {
      response = await runChunkedBulkRequest({
        url: "/accounting/additional-transactions/bulk",
        method: "POST",
        items: itemsToSave,
        buildPayload: (chunk) => ({
          atomic: false,
          items: chunk.map((item) => ({
            date: item.date,
            section,
            entry_type: "debit",
            name: item.description,
            amount: Number(item.amount) || 0,
            description: item.description,
            notes: null,
            reservation_id: item.reservationId || null,
            reservation_income_id: item.reservationIncomeId || null,
          })),
        }),
      });
    } catch (error) {
      setProgress({ active: false, current: 0, total: 0 });
      Swal.fire("Gagal", error?.message || "Gagal menyimpan ke Trf.", "error");
      return;
    }
    setProgress({ active: false, current: 0, total: 0 });

    const failures = getBulkFailures(response.results, itemsToSave, (item) => item?.description || "-");
    // DUPLICATE = transaksi sudah ada di tab tujuan (termasuk retry yang sebenarnya sudah tersimpan).
    const duplicates = failures.filter((failure) => failure.result.status === "duplicate");
    const realFailures = failures.filter((failure) => failure.result.status !== "duplicate");
    const savedCount = response.okCount;

    if (failures.length === 0) {
      Swal.fire("Berhasil", `${savedCount} transaksi ditambahkan ke ${targetLabel}.`, "success");
      onDone();
      return;
    }

    Swal.fire({
      icon: realFailures.length ? (savedCount > 0 ? "warning" : "error") : "info",
      title: `${savedCount} tersimpan${duplicates.length ? `, ${duplicates.length} sudah ada` : ""}${realFailures.length ? `, ${realFailures.length} gagal` : ""}`,
      html: buildBulkFailureHtml(failures),
    });
    if (savedCount > 0 || realFailures.length === 0) onDone();
  };

  const busy = progress.active;

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered size="xl">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Tambah ke Trf ({includedItems.length} dari {items.length} transaksi)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold small">Tujuan Pembukuan</Form.Label>
            <Form.Select value={section} onChange={(event) => setSection(event.target.value)} disabled={busy} style={{ maxWidth: 280 }}>
              {TRF_TARGETS.map((target) => (
                <option key={target.section} value={target.section}>{target.label}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Disimpan sebagai Debet di tab {targetLabel} pada Menu Pembukuan.
            </Form.Text>
          </Form.Group>

          {duplicateCheck.loading && (
            <Alert variant="light" className="py-2 small d-flex align-items-center gap-2 border">
              <Spinner animation="border" size="sm" /> Mengecek apakah transaksi sudah ada di Trf...
            </Alert>
          )}
          {duplicateCheck.error && (
            <Alert variant="warning" className="py-2 small">
              Tidak bisa mengecek data dobel ({duplicateCheck.error}). Pastikan sendiri transaksi belum ada di {targetLabel}.
            </Alert>
          )}
          {!duplicateCheck.loading && duplicateInTargetCount > 0 && (
            <Alert variant="danger" className="py-2 small">
              <strong>{duplicateInTargetCount} transaksi sudah ada di {targetLabel}</strong> dan otomatis tidak dicentang
              supaya tidak dobel. Centang manual kalau memang ingin disalin lagi.
            </Alert>
          )}

          <div style={{ maxHeight: "50vh", overflowY: "auto" }} data-excel-grid>
            <div className="text-muted small mb-1">
              Navigasi seperti Excel: Enter / Tab / panah untuk pindah sel.
            </div>
            <Table size="sm" bordered className="align-middle mb-0" style={{ fontSize: 12 }}>
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ width: 40 }} className="text-center">
                    <Form.Check
                      type="checkbox"
                      aria-label="Pilih semua"
                      title="Pilih semua"
                      checked={items.length > 0 && includedItems.length === items.length}
                      onChange={(event) => setItems((current) => current.map((item) => ({ ...item, include: event.target.checked })))}
                      disabled={busy}
                    />
                  </th>
                  <th style={{ width: 40 }} className="text-center">NO</th>
                  <th style={{ width: 150 }}>TANGGAL</th>
                  <th>TRANSAKSI</th>
                  <th style={{ width: 150 }} className="text-end">NOMINAL</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const matches = itemMatches[index] || { inTarget: [], inOthers: [] };
                  return (
                  <tr
                    key={item.reservationId || index}
                    className={item.include ? (matches.inTarget.length ? "table-danger" : "") : "text-muted"}
                    style={item.include ? undefined : { opacity: 0.55 }}
                  >
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        aria-label={`Salin ${item.description}`}
                        checked={item.include}
                        onChange={(event) => updateItem(index, "include", event.target.checked)}
                        disabled={busy}
                      />
                    </td>
                    <td className="text-center fw-bold">{index + 1}</td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="date"
                        value={item.date}
                        onChange={(event) => updateItem(index, "date", event.target.value)}
                        onClick={(event) => event.currentTarget.showPicker?.()}
                        onKeyDown={(event) => handleExcelGridKeyDown(event, GRID_COLUMNS)}
                        data-grid-row={index}
                        data-grid-col="date"
                        disabled={busy}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={item.description}
                        onChange={(event) => updateItem(index, "description", event.target.value)}
                        onKeyDown={(event) => handleExcelGridKeyDown(event, GRID_COLUMNS)}
                        data-grid-row={index}
                        data-grid-col="description"
                        disabled={busy}
                      />
                      {(matches.inTarget.length > 0 || matches.inOthers.length > 0) && (
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {matches.inTarget.length > 0 && (
                            <Badge bg="danger">Sudah ada di {targetLabel}</Badge>
                          )}
                          {[...new Set(matches.inOthers.map((match) => match.section))].map((otherSection) => (
                            <Badge key={otherSection} bg="warning" text="dark">Ada di {getTrfSectionLabel(otherSection)}</Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        inputMode="numeric"
                        className="text-end fw-bold"
                        value={item.amount ? (focusedAmountIndex === index ? String(item.amount) : formatNumber(item.amount)) : ""}
                        placeholder="Kosong"
                        onChange={(event) => updateItem(index, "amount", parseNumberInput(event.target.value))}
                        onFocus={(event) => {
                          setFocusedAmountIndex(index);
                          const input = event.currentTarget;
                          window.requestAnimationFrame(() => input.select());
                        }}
                        onBlur={() => setFocusedAmountIndex(null)}
                        onKeyDown={(event) => handleExcelGridKeyDown(event, GRID_COLUMNS)}
                        data-grid-row={index}
                        data-grid-col="amount"
                        disabled={busy}
                      />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={4} className="text-end">TOTAL ({includedItems.length} dicentang)</td>
                  <td className="text-end">{formatNumber(totalAmount)}</td>
                </tr>
              </tfoot>
            </Table>
          </div>

          {busy && (
            <Alert variant="light" className="mt-3 mb-0 py-2 small d-flex align-items-center gap-2 border">
              <Spinner animation="border" size="sm" /> Menyimpan {progress.total} transaksi ke {targetLabel}...
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="border" onClick={onHide} disabled={busy}>Batal</Button>
          <Button type="submit" variant="primary" disabled={busy || duplicateCheck.loading || includedItems.length === 0}>
            {busy ? "Menyimpan..." : `Simpan ke ${targetLabel}`}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AccountingV2TrfModal;
